import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import LanguageToggle from "@/components/LanguageToggle";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft,
  User, Phone, Ruler, Weight, Heart, Stethoscope, GraduationCap, Briefcase, Loader2, AlertCircle,
  Camera, FileText, Upload, Info
} from "lucide-react";

type Role = "patient" | "dietitian" | "doctor";
type Step = "role" | "credentials" | "profile";

const SignupPage = () => {
  const { language } = useLanguage();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const bn = language === "bn";

  const [step, setStep] = useState<Step>("role");
  const [role, setRole] = useState<Role>("patient");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");

  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [healthProfile, setHealthProfile] = useState("");

  const [dName, setDName] = useState("");
  const [qualification, setQualification] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [experience, setExperience] = useState("");
  const [fee, setFee] = useState("");
  const [bio, setBio] = useState("");

  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string | null>(null);
  const [verificationDoc, setVerificationDoc] = useState<File | null>(null);

  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePhoto(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfilePhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleVerificationDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError(bn ? "শুধুমাত্র PDF ফাইল আপলোড করুন" : "Only PDF files are accepted");
      return;
    }
    setVerificationDoc(file);
    setError("");
  };

  const uploadFile = async (bucket: string, path: string, file: File): Promise<string | null> => {
    const { data, error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
    if (error) return null;
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);
    return urlData.publicUrl;
  };

  const goBack = () => {
    if (step === "profile") setStep("credentials");
    else if (step === "credentials") setStep("role");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if ((role === "doctor" || role === "dietitian") && !profilePhoto) {
      setError(bn ? "প্রোফাইল ছবি আপলোড করুন" : "Please upload your profile photo");
      return;
    }
    if ((role === "doctor" || role === "dietitian") && !verificationDoc) {
      setError(bn ? "যাচাইকরণ ডকুমেন্ট PDF আপলোড করুন" : "Please upload your verification documents PDF");
      return;
    }

    setLoading(true);
    try {
      const fullName = role === "patient" ? name : dName;
      const metadata: Record<string, unknown> = {
        role,
        full_name: fullName,
        phone,
      };

      const result = await signup(email, password, metadata);
      if (result.error) {
        setError(bn ? "সাইন আপ ব্যর্থ হয়েছে। আবার চেষ্টা করুন।" : result.error);
        setLoading(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1200));

      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const profileUpdate: Record<string, unknown> = { phone, full_name: fullName };

        if (role === "patient") {
          Object.assign(profileUpdate, {
            age: Number(age) || null,
            gender,
            height: Number(height) || null,
            weight: Number(weight) || null,
            health_profile: healthProfile,
          });
        } else {
          Object.assign(profileUpdate, {
            qualification,
            specialization,
            experience: Number(experience) || null,
            fee: Number(fee) || null,
            bio,
          });

          if (profilePhoto) {
            const photoUrl = await uploadFile(
              "profile-photos",
              `practitioners/${authUser.id}/photo_${Date.now()}`,
              profilePhoto
            );
            if (photoUrl) profileUpdate.profile_photo = photoUrl;
          }

          if (verificationDoc) {
            const docUrl = await uploadFile(
              "verification-docs",
              `practitioners/${authUser.id}/verification_${Date.now()}.pdf`,
              verificationDoc
            );
            if (docUrl) profileUpdate.address = docUrl;
          }
        }

        const { error: profileError } = await supabase
          .from("profiles")
          .update(profileUpdate)
          .eq("user_id", authUser.id);
        if (profileError) throw profileError;
      }
    } catch {
      setError(bn ? "সাইন আপ ব্যর্থ হয়েছে। আবার চেষ্টা করুন।" : "Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const slideVariants = {
    enter: { x: 80, opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: -80, opacity: 0 },
  };

  const doctorPdfInstructions = [
    bn ? "BMDC রেজিস্ট্রেশন সার্টিফিকেট" : "BMDC Registration Certificate",
    bn ? "মেডিকেল ডিগ্রি সার্টিফিকেট (MBBS / FCPS ইত্যাদি)" : "Medical Degree Certificate (MBBS / FCPS etc.)",
    bn ? "জাতীয় পরিচয়পত্র (NID)" : "National ID Card (NID)",
  ];

  const dietitianPdfInstructions = [
    bn ? "পুষ্টি বিজ্ঞান ডিগ্রি সার্টিফিকেট" : "Nutrition Science Degree Certificate",
    bn ? "অতিরিক্ত বিশেষায়িত কোর্স সার্টিফিকেট (যদি থাকে)" : "Additional Specialisation Course Certificates (if any)",
    bn ? "জাতীয় পরিচয়পত্র (NID)" : "National ID Card (NID)",
  ];

  const pdfInstructions = role === "doctor" ? doctorPdfInstructions : dietitianPdfInstructions;

  return (
    <div className="min-h-screen bg-[#fbfaf7] px-4 py-5 sm:px-6 sm:py-8">
      <div className="relative mx-auto flex w-full max-w-3xl flex-col overflow-hidden rounded-[32px] bg-[#f0f8f4] px-5 pb-5 pt-7 shadow-float sm:px-8 sm:pt-9">
        <div className="absolute -left-10 -top-10 h-40 w-40 rounded-full bg-[#e5f2ed]" />
        <div className="absolute bottom-12 -right-8 h-32 w-32 rounded-full bg-[#e5f2ed]/70" />
        <div className="relative z-10 mb-6 flex items-center justify-between">
          {step !== "role" ? (
            <button type="button" onClick={goBack} className="p-3 -ml-2 text-[#287d73] active:opacity-70">
              <ArrowLeft className="h-6 w-6" />
            </button>
          ) : <div />}
          <LanguageToggle />
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center">
          <img src="/logo.png" alt="Sebaco" className="mb-2 h-16 w-16 rounded-2xl border border-[#dfe9e5] bg-white p-2 object-contain" />
          <h1 className="font-serif text-2xl font-extrabold leading-tight text-[#18544e]">
            {step === "role" ? (bn ? "আপনি কে?" : "Who are you?")
              : step === "credentials" ? (bn ? "অ্যাকাউন্ট তৈরি করুন" : "Create Account")
              : (bn ? "প্রোফাইল সম্পূর্ণ করুন" : "Complete Profile")}
          </h1>
          <p className="mt-1 text-sm text-[#697273]">
            {step === "role" ? (bn ? "আপনার ভূমিকা নির্বাচন করুন" : "Select your role to get started")
              : step === "credentials" ? (bn ? "আপনার লগইন তথ্য দিন" : "Enter your login details")
              : role === "patient" ? (bn ? "আপনার স্বাস্থ্য তথ্য দিন" : "Tell us about your health")
              : (bn ? "আপনার পেশাদার তথ্য দিন" : "Your professional details")}
          </p>
        </motion.div>
        <div className="mt-4 flex gap-2">
          {["role", "credentials", "profile"].map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-all ${["role", "credentials", "profile"].indexOf(step) >= i ? "bg-[#27887e]" : "bg-[#dce9e4]"}`} />
          ))}
        </div>
      </div>

      <div className="mx-auto mt-5 w-full max-w-3xl px-5 sm:mt-6">
        <AnimatePresence mode="wait">
          {step === "role" && (
            <motion.div key="role" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="soft-surface rounded-2xl p-6">
              <div className="space-y-4">
                {(["patient", "dietitian", "doctor"] as const).map((r) => (
                  <motion.button key={r} onClick={() => { setRole(r); setStep("credentials"); }}
                    className={`w-full rounded-2xl border-2 p-4 text-left transition-colors ${role === r ? "border-[#287d73] bg-[#f7fbf9]" : "border-[#e2ebe7] bg-white"}`}
                    whileTap={{ scale: 0.97 }}>
                    <div className="flex items-center gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${role === r ? "bg-[#2caaa0] text-white" : "bg-[#e2f1ec] text-[#287d73]"}`}>
                        {r === "patient" ? <User className="h-5 w-5" /> : <Stethoscope className="h-5 w-5" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-extrabold text-[#18544e]">
                          {r === "patient" ? (bn ? "রোগী" : "Patient") : r === "dietitian" ? (bn ? "ডায়েটিশিয়ান" : "Dietitian") : (bn ? "ডাক্তার" : "Doctor")}
                        </h3>
                        <p className="mt-0.5 max-w-[200px] text-[10px] leading-4 text-[#697273]">
                          {r === "patient" ? (bn ? "পুষ্টি ও চিকিৎসা পরামর্শ নিন" : "Get nutrition & medical counseling")
                            : r === "dietitian" ? (bn ? "পুষ্টি পরামর্শ দিন" : "Provide nutrition plans")
                            : (bn ? "চিকিৎসা পরামর্শ দিন" : "Prescribe & treat patients")}
                        </p>
                      </div>
                      <ArrowRight className="ml-auto h-5 w-5 text-[#18544e]" />
                    </div>
                  </motion.button>
                ))}
              </div>
              <p className="mt-5 text-center text-xs text-[#697273]">
                {bn ? "ইতিমধ্যে অ্যাকাউন্ট আছে?" : "Already have an account?"}{" "}
                <Link to="/login" className="font-bold text-[#287d73]">{bn ? "লগইন করুন" : "Log in"}</Link>
              </p>
            </motion.div>
          )}

          {step === "credentials" && (
            <motion.div key="credentials" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="glass rounded-2xl p-6 shadow-float">
              <form onSubmit={(e) => { e.preventDefault(); setStep("profile"); }} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "ইমেইল" : "Email"}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={bn ? "আপনার ইমেইল" : "your@email.com"} className="w-full pl-10 pr-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "ফোন নম্বর" : "Phone Number"}</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+880 1XXX-XXXXXX" className="w-full pl-10 pr-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "পাসওয়ার্ড" : "Password"}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required minLength={6} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                      {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  </div>
                </div>
                <motion.button type="submit" className="w-full gradient-primary text-primary-foreground py-3.5 rounded-xl font-bold text-sm shadow-glow flex items-center justify-center gap-2" whileTap={{ scale: 0.97 }}>
                  {bn ? "পরবর্তী" : "Next"} <ArrowRight className="h-4 w-4" />
                </motion.button>
              </form>
            </motion.div>
          )}

          {step === "profile" && (
            <motion.div key="profile" variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }} className="glass rounded-2xl p-6 shadow-float mb-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                {role === "patient" ? (
                  <>
                    <div>
                      <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "পুরো নাম" : "Full Name"}</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={bn ? "আপনার নাম" : "Your full name"} className="w-full pl-10 pr-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "বয়স" : "Age"}</label>
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="32" className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "লিঙ্গ" : "Gender"}</label>
                        <select value={gender} onChange={(e) => setGender(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30" required>
                          <option value="">{bn ? "নির্বাচন" : "Select"}</option>
                          <option value="male">{bn ? "পুরুষ" : "Male"}</option>
                          <option value="female">{bn ? "মহিলা" : "Female"}</option>
                          <option value="other">{bn ? "অন্যান্য" : "Other"}</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-muted-foreground mb-1.5 block flex items-center gap-1"><Ruler className="h-3 w-3" /> {bn ? "উচ্চতা (সেমি)" : "Height (cm)"}</label>
                        <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="170" className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-muted-foreground mb-1.5 block flex items-center gap-1"><Weight className="h-3 w-3" /> {bn ? "ওজন (কেজি)" : "Weight (kg)"}</label>
                        <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="78" className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-muted-foreground mb-1.5 block flex items-center gap-1"><Heart className="h-3 w-3" /> {bn ? "স্বাস্থ্য প্রোফাইল" : "Health Profile"}</label>
                      <textarea value={healthProfile} onChange={(e) => setHealthProfile(e.target.value)} placeholder={bn ? "আপনার স্বাস্থ্য সমস্যা লিখুন..." : "Describe any health conditions..."} rows={3} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground resize-none" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "পুরো নাম" : "Full Name"}</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input type="text" value={dName} onChange={(e) => setDName(e.target.value)}
                          placeholder={role === "doctor" ? (bn ? "Dr. আপনার নাম" : "Dr. Your Name") : (bn ? "Dt. আপনার নাম" : "Dt. Your Name")}
                          className="w-full pl-10 pr-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-muted-foreground mb-1.5 block flex items-center gap-1"><GraduationCap className="h-3 w-3" /> {bn ? "যোগ্যতা" : "Qualification"}</label>
                      <input type="text" value={qualification} onChange={(e) => setQualification(e.target.value)}
                        placeholder={role === "doctor" ? "MBBS, FCPS" : "MSc in Nutrition, RDN"}
                        className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-muted-foreground mb-1.5 block flex items-center gap-1"><Stethoscope className="h-3 w-3" /> {bn ? "বিশেষত্ব" : "Specialization"}</label>
                      {role === "doctor" ? (
                        <select value={specialization} onChange={(e) => setSpecialization(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30" required>
                          <option value="">{bn ? "নির্বাচন করুন" : "Select Specialty"}</option>
                          <option value="general">{bn ? "সাধারণ চিকিৎসা" : "General Medicine"}</option>
                          <option value="cardiology">{bn ? "কার্ডিওলজি" : "Cardiology"}</option>
                          <option value="gastro">{bn ? "গ্যাস্ট্রোএন্টারোলজি" : "Gastroenterology"}</option>
                          <option value="endo">{bn ? "এন্ডোক্রাইনোলজি" : "Endocrinology"}</option>
                          <option value="neuro">{bn ? "নিউরোলজি" : "Neurology"}</option>
                          <option value="ortho">{bn ? "অর্থোপেডিক্স" : "Orthopedics"}</option>
                        </select>
                      ) : (
                        <select value={specialization} onChange={(e) => setSpecialization(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30" required>
                          <option value="">{bn ? "নির্বাচন করুন" : "Select Specialty"}</option>
                          <option value="diabetes">{bn ? "ডায়াবেটিস" : "Diabetes Management"}</option>
                          <option value="weight">{bn ? "ওজন ব্যবস্থাপনা" : "Weight Management"}</option>
                          <option value="pcos">{bn ? "পিসিওএস" : "PCOS & Hormonal"}</option>
                          <option value="pediatric">{bn ? "শিশু পুষ্টি" : "Pediatric Nutrition"}</option>
                          <option value="sports">{bn ? "স্পোর্টস পুষ্টি" : "Sports Nutrition"}</option>
                          <option value="clinical">{bn ? "ক্লিনিক্যাল" : "Clinical Nutrition"}</option>
                        </select>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold text-muted-foreground mb-1.5 block flex items-center gap-1"><Briefcase className="h-3 w-3" /> {bn ? "অভিজ্ঞতা (বছর)" : "Experience (yrs)"}</label>
                        <input type="number" value={experience} onChange={(e) => setExperience(e.target.value)} placeholder={role === "doctor" ? "10" : "5"} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                      </div>
                      <div>
                        <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "ফি (৳)" : "Fee (৳)"}</label>
                        <input type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder={role === "doctor" ? "1200" : "800"} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" required />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-muted-foreground mb-1.5 block">{bn ? "সংক্ষিপ্ত বিবরণ" : "Short Bio"}</label>
                      <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder={bn ? "আপনার সম্পর্কে লিখুন..." : "Tell patients about yourself..."} rows={2} className="w-full px-4 py-3 rounded-xl bg-background text-foreground text-sm font-medium border border-border outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground resize-none" />
                    </div>

                    <div className="border-t border-border/40 pt-4">
                      <p className="text-xs font-extrabold text-foreground mb-3 flex items-center gap-1.5">
                        <Camera className="h-3.5 w-3.5 text-primary" />
                        {bn ? "প্রোফাইল ছবি" : "Profile Photo"}
                        <span className="text-destructive">*</span>
                      </p>
                      <label className="block cursor-pointer">
                        <input type="file" accept="image/*" onChange={handleProfilePhotoChange} className="hidden" />
                        <div className="flex items-center gap-3">
                          <div className={`h-16 w-16 rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 transition-colors ${profilePhotoPreview ? "border-primary" : "border-border hover:border-primary/50"}`}>
                            {profilePhotoPreview ? (
                              <img src={profilePhotoPreview} alt="preview" className="h-full w-full object-cover" />
                            ) : (
                              <Camera className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-primary">{bn ? "ছবি বেছে নিন" : "Choose photo"}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{bn ? "JPG, PNG — সর্বোচ্চ 5MB" : "JPG, PNG — max 5MB"}</p>
                          </div>
                        </div>
                      </label>
                    </div>

                    <div>
                      <p className="text-xs font-extrabold text-foreground mb-2 flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5 text-primary" />
                        {bn ? "যাচাইকরণ ডকুমেন্ট (PDF)" : "Verification Documents (PDF)"}
                        <span className="text-destructive">*</span>
                      </p>
                      <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 mb-3">
                        <p className="text-[10px] font-bold text-primary flex items-center gap-1 mb-1.5">
                          <Info className="h-3 w-3" />
                          {bn ? "PDF-এ যা অন্তর্ভুক্ত করতে হবে:" : "Include in your PDF:"}
                        </p>
                        <ul className="space-y-1">
                          {pdfInstructions.map((item, i) => (
                            <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1.5">
                              <span className="text-primary font-bold mt-0.5">{i + 1}.</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <label className="block cursor-pointer">
                        <input type="file" accept="application/pdf" onChange={handleVerificationDocChange} className="hidden" />
                        <div className={`rounded-xl border-2 border-dashed p-4 flex items-center gap-3 transition-colors ${verificationDoc ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${verificationDoc ? "bg-primary/15" : "bg-muted"}`}>
                            {verificationDoc ? <FileText className="h-5 w-5 text-primary" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            {verificationDoc ? (
                              <>
                                <p className="text-xs font-bold text-primary truncate">{verificationDoc.name}</p>
                                <p className="text-[10px] text-muted-foreground">{(verificationDoc.size / 1024).toFixed(0)} KB</p>
                              </>
                            ) : (
                              <>
                                <p className="text-xs font-bold">{bn ? "PDF আপলোড করুন" : "Upload PDF"}</p>
                                <p className="text-[10px] text-muted-foreground">{bn ? "সকল ডকুমেন্ট একটি PDF-এ রাখুন" : "Combine all documents into one PDF"}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  </>
                )}

                {error && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                    <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <p className="text-xs font-medium text-destructive">{error}</p>
                  </motion.div>
                )}

                <motion.button type="submit" disabled={loading} className="w-full gradient-primary text-primary-foreground py-3.5 rounded-xl font-bold text-sm shadow-glow flex items-center justify-center gap-2 disabled:opacity-50" whileTap={{ scale: 0.97 }}>
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" />{bn ? "তৈরি হচ্ছে..." : "Creating..."}</> : <>{bn ? "অ্যাকাউন্ট তৈরি করুন" : "Create Account"} <ArrowRight className="h-4 w-4" /></>}
                </motion.button>

                <p className="text-center text-xs text-muted-foreground">
                  {bn ? "ইতিমধ্যে অ্যাকাউন্ট আছে?" : "Already have an account?"}{" "}
                  <Link to="/login" className="text-primary font-bold">{bn ? "লগইন করুন" : "Log in"}</Link>
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SignupPage;
